import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

// Success checkmark animation component
function SuccessAnimation() {
  return (
    <div className="flex flex-col items-center gap-4 animate-fade-in">
      <div className="relative">
        {/* Outer glow ring */}
        <div className="absolute inset-0 bg-emerald-500/30 rounded-full animate-ping" />
        {/* Green circle */}
        <div className="relative w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center animate-scale-in">
          {/* Checkmark */}
          <svg 
            className="w-8 h-8 text-white animate-draw-check" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor" 
            strokeWidth={3}
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              d="M5 13l4 4L19 7" 
              style={{
                strokeDasharray: 24,
                strokeDashoffset: 24,
                animation: 'drawCheck 0.4s ease-out 0.2s forwards'
              }}
            />
          </svg>
        </div>
      </div>
      <p className="text-emerald-400 text-sm font-medium">
        You're on the list!
      </p>
    </div>
  );
}

export default function LandingV2() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error, exists
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      // Check if email already exists
      const { data: existing, error: checkError } = await supabase
        .from('waitlist')
        .select('email')
        .eq('email', email.toLowerCase().trim())
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existing) {
        setStatus('exists');
        return;
      }

      // Insert new email
      const { error: insertError } = await supabase
        .from('waitlist')
        .insert([{ email: email.toLowerCase().trim() }]);

      if (insertError) throw insertError;

      setStatus('success');
      setEmail('');
    } catch (err) {
      console.error('Error:', err);
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-black">
      {/* Custom styles for animations */}
      <style>{`
        @keyframes drawCheck {
          to {
            stroke-dashoffset: 0;
          }
        }
        @keyframes scale-in {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.4s ease-out forwards;
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        .title-glow {
          transition: text-shadow 0.1s ease-out;
        }
        .title-glow:hover {
          text-shadow: 0 0 10px rgba(255,255,255,0.6), 0 0 20px rgba(255,255,255,0.4);
        }
      `}</style>


      {/* Header */}
      <header className="relative z-10 p-4 md:p-6">
        <img
          src="/logo.png"
          alt="Instnce"
          className="w-10 h-10 md:w-12 md:h-12 invert"
        />
      </header>

      {/* Main content */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-start px-5 mt-4">
        <div className="w-full max-w-3xl text-center">
          {/* Big Logo Text */}
          <div className="mb-2">
            <h1 className="inline text-5xl md:text-7xl lg:text-8xl font-black tracking-tight text-white title-glow cursor-default">
              INSTNCE
            </h1>
          </div>

          {/* Subheader */}
          <p className="text-sm md:text-base text-white/80 mb-8 max-w-lg mx-auto leading-relaxed font-semibold">
            Join the waitlist now for access to the Beta
          </p>

          {/* Form or Success Animation */}
          {status === 'success' ? (
            <SuccessAnimation />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  disabled={status === 'loading'}
                  className="flex-1 px-4 py-3 bg-transparent border-b border-white/70 text-white text-base placeholder-white/80 focus:outline-none focus:border-white transition-all disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-8 py-3 bg-white/20 border border-white/70 text-white text-base rounded-full font-medium hover:bg-white hover:text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? '...' : 'Join'}
                </button>
              </div>

              {/* Status messages */}
              {status === 'exists' && (
                <p className="text-white/60 text-sm">
                  This email is already on the waitlist.
                </p>
              )}
              {status === 'error' && (
                <p className="text-red-400 text-sm font-medium">
                  {errorMessage}
                </p>
              )}
            </form>
          )}

          {/* App Store Coming Soon */}
          <div className="mt-10 flex flex-col items-center gap-3">
            <p className="text-white/90 text-sm tracking-widest uppercase">Coming soon to</p>
            <img
              src="/app_store_badge.png"
              alt="Download on the App Store"
              className="h-12 opacity-90 grayscale hover:opacity-100 hover:grayscale-0 transition-all cursor-pointer"
            />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 p-6 md:p-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs tracking-wide text-white/40">
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
          <div className="flex items-center gap-4">
            <span>© 2026 Instnce</span>
            <a 
              href="https://x.com/Instncex" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
